<template>
  <div class="relative w-full overflow-visible group/rcviewer">
    <div
      v-on-click-outside="() => $refs[refName]?.hide()"
      class="markdown-content w-full relative overflow-hidden rounded-xl"
      :class="[
        { 'is-streaming': isStreaming },
        refName === 'authorHolder'
          ? 'theme-emerald'
          : refName === 'patentHolder'
            ? 'theme-purple'
            : refName === 'companyHolder'
              ? 'theme-amber'
              : 'theme-blue',
      ]"
      @mouseover="hover"
      @mouseout="leave"
      @click="click"
    >
      <markdown-viewer
        v-if="textWithLinks"
        :text="textWithLinks"
        class="w-full text-gray-800 dark:text-gray-200 overflow-x-auto overflow-y-hidden custom-scrollbar relative z-10"
      />

      <!-- Visual Scroll Indicators (Only if a table is present and enabled) -->
      <template v-if="hasTable && showTableScrollIndicators">
        <div
          class="absolute left-0 top-6 bottom-8 w-8 bg-gradient-to-r pointer-events-none opacity-0 group-hover/rcviewer:opacity-100 transition-opacity z-20"
          :class="[
            refName === 'authorHolder'
              ? 'from-emerald-600/10 dark:from-emerald-400/10'
              : refName === 'patentHolder'
                ? 'from-purple-600/10 dark:from-purple-400/10'
                : refName === 'companyHolder'
                  ? 'from-amber-600/10 dark:from-amber-400/10'
                  : 'from-blue-600/10 dark:from-blue-400/10',
            'to-transparent',
          ]"
        ></div>
        <div
          class="absolute right-0 top-6 bottom-8 w-8 bg-gradient-to-l pointer-events-none opacity-0 group-hover/rcviewer:opacity-100 transition-opacity z-20"
          :class="[
            refName === 'authorHolder'
              ? 'from-emerald-600/10 dark:from-emerald-400/10'
              : refName === 'patentHolder'
                ? 'from-purple-600/10 dark:from-purple-400/10'
                : refName === 'companyHolder'
                  ? 'from-amber-600/10 dark:from-amber-400/10'
                  : 'from-blue-600/10 dark:from-blue-400/10',
            'to-transparent',
          ]"
        ></div>

        <!-- Small floating badge -->
        <div
          class="absolute right-4 top-10 px-2 py-1 text-white text-[9px] font-bold uppercase tracking-widest rounded shadow-lg pointer-events-none opacity-0 group-hover/rcviewer:opacity-100 transition-all transform translate-y-2 group-hover/rcviewer:translate-y-0 z-30 flex items-center gap-1.5"
          :class="[
            refName === 'authorHolder'
              ? 'bg-emerald-600/90'
              : refName === 'patentHolder'
                ? 'bg-purple-600/90'
                : refName === 'companyHolder'
                  ? 'bg-amber-600/90'
                  : 'bg-blue-600/90',
          ]"
        >
          <i class="fas fa-arrows-left-right"></i>
          Scroll Table
        </div>
      </template>
    </div>

    <!-- The Single Shared Popover Instance -->
    <template v-if="refName === 'authorHolder'">
      <research-compass-author-popover
        :ref="refName"
        :question="question"
        @scroll-to-expert="scrollToCitation"
      />
    </template>
    <template v-else-if="refName === 'companyHolder'">
      <research-compass-industry-partner-popover
        :ref="refName"
        :question="question"
        @scroll-to-partner="scrollToCitation"
      />
    </template>
    <template v-else>
      <research-compass-source-popover
        :ref="refName"
        :sources="sources"
        :patents="patents"
        :is-patent="refName === 'patentHolder'"
        @scroll-to-citation="scrollToCitation"
      />
    </template>
  </div>
</template>

<script>
import { vOnClickOutside } from "@vueuse/components";
import { nextTick } from "vue";
import * as utils from "../../utils";
import MarkdownViewer from "../MarkdownViewer.vue";
import ResearchCompassSourcePopover from "./ResearchCompassSourcePopover.vue";
import ResearchCompassAuthorPopover from "./ResearchCompassAuthorPopover.vue";
import ResearchCompassIndustryPartnerPopover from "./ResearchCompassIndustryPartnerPopover.vue";

const { avatarUrlFromString } = utils;

export default {
  components: {
    MarkdownViewer,
    ResearchCompassSourcePopover,
    ResearchCompassAuthorPopover,
    ResearchCompassIndustryPartnerPopover,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  props: {
    text: String,
    sources: Array,
    patents: Array,
    question: Object,
    refName: {
      type: String,
      default: "sourceHolder",
    },
    showTableScrollIndicators: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isStreaming() {
      return this.text && this.text.includes("\u258c");
    },
  },
  data() {
    return {
      textWithLinks: "",
      hasTable: false,
    };
  },
  watch: {
    text() {
      this.setUpLinks();
    },
  },
  mounted() {
    this.setUpLinks();
  },
  methods: {
    setUpLinks() {
      const isPatentTab = this.refName === "patentHolder";

      let result = this.text || "";

      // Replace unicode block cursor with a styled inline cursor element
      result = result.replace(/\u258c/g, '<span class="streaming-cursor"></span>');

      // 0. Pre-process to split multiple citations in one bracket [1, 2] -> [1] [2]
      const splitRegex = /\[([0-9, ]+?)\]/g;
      const splitMatches = result.match(splitRegex);
      if (splitMatches) {
        [...new Set(splitMatches)].forEach((match) => {
          if (match.indexOf(",") > -1) {
            const parts = match
              .slice(1, -1)
              .split(",")
              .map((p) => p.trim());
            const splitCitations = parts.map((p) => `[${p}]`).join(" ");
            result = result.replaceAll(match, splitCitations);
          }
        });
      }

      // Match anything in square brackets
      const regex = /\[([^\]]+)\]/g;
      const matches = result.match(regex);

      if (matches) {
        const uniqueMatches = [...new Set(matches)];
        uniqueMatches.forEach((match) => {
          const content = match.slice(1, -1);
          let linkText = content;
          let prefix = "#rcref-";
          let title = "";

          // 1. Check for Authors
          if (this.question?.authors) {
            const author = Object.values(this.question.authors).find(
              (a) => a.name.toLowerCase() === content.toLowerCase(),
            );
            if (author) {
              linkText = " ";
              prefix = "#rcauthor-";
              title = avatarUrlFromString(author.name);
            }
          }

          // 2. Check for Companies (Industry Partners)
          if (prefix === "#rcref-" && this.question?.industryPartners) {
            const partner = this.question.industryPartners.find(
              (p) => p.name.toLowerCase() === content.toLowerCase(),
            );
            if (partner) {
              linkText = " ";
              prefix = "#rccompany-";
              title = partner.logoUrl || "";
            }
          }

          // 3. Handle Numbers (Papers or Patents)
          if (prefix === "#rcref-" && /^\d+$/.test(content)) {
            // If we're in the patent tab, these numbers are patents
            if (isPatentTab) {
              prefix = "#rcpatent-";
            } else {
              // Otherwise they are sources
              prefix = "#rcref-";
            }
          }

          // 4. Fallback: Descriptive strings like "Paper 1"
          if (
            prefix === "#rcref-" &&
            content.toLowerCase().startsWith("paper ")
          ) {
            prefix = "#rcref-";
          }

          // Apply replacement
          if (prefix === "#rcauthor-" || prefix === "#rccompany-") {
            result = result.replaceAll(
              match,
              `[${linkText}](${prefix}${encodeURIComponent(
                content,
              )} "${title}") **${content}**`,
            );
          } else {
            result = result.replaceAll(
              match,
              `[${linkText}](${prefix}${encodeURIComponent(content)})`,
            );
          }
        });
      }
      this.textWithLinks = result;

      if (this.showTableScrollIndicators) {
        nextTick(() => {
          if (this.$el) {
            this.hasTable = this.$el.querySelector("table") !== null;
          }
        });
      }
    },
    hover(event) {
      const target = event?.target;
      if (!target || !target.dataset || !target.dataset.rcRefId) return;

      if (
        target.rel === "popover" ||
        target.rel === "popover-author" ||
        target.rel === "popover-company"
      ) {
        const currentRef = this.$refs[this.refName];
        if (!currentRef) return;

        const refId = target.dataset.rcRefId
          .replace("#rcref-", "")
          .replace("#rcauthor-", "")
          .replace("#rccompany-", "")
          .replace("#rcpatent-", "");

        if (refId !== currentRef.refId) {
          currentRef.refId = refId;
          currentRef.load();
        }

        const rect = target.getBoundingClientRect();
        currentRef.show(rect);
      }
    },
    leave(event) {
      const target = event?.target;
      if (!target || !target.dataset || !target.dataset.rcRefId) return;

      const currentRef = this.$refs[this.refName];
      if (currentRef) {
        currentRef.mouseIsOver = false;
        setTimeout(() => {
          if (!currentRef.mouseIsOver) {
            currentRef.hide();
          }
        }, 200);
      }
    },
    click(event) {
      const target = event?.target;
      if (!target || !target.dataset || !target.dataset.rcRefId) return;

      event.preventDefault();
      event.stopPropagation();

      const refId = target.dataset.rcRefId
        .replace("#rcref-", "")
        .replace("#rcauthor-", "")
        .replace("#rccompany-", "")
        .replace("#rcpatent-", "");

      this.scrollToCitation(refId);
      this.$refs[this.refName]?.hideIfNotHover();
    },
    scrollToCitation(citationIndex) {
      this.$emit("scroll-to-citation", citationIndex);
    },
  },
};
</script>

<style scoped>
.markdown-content :deep(a[data-rc-ref-id]) {
  text-decoration: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  vertical-align: middle !important;
  cursor: pointer !important;
}

.markdown-content :deep(> div > *:first-child) {
  margin-top: 0 !important;
}

/* Authors: Round avatars */
.markdown-content :deep(a[data-rc-ref-id^="#rcauthor-"]) {
  width: 1.5rem !important;
  height: 1.5rem !important;
  border-radius: 9999px !important;
  background-size: cover !important;
  background-position: center !important;
  border: 1px solid rgba(229, 231, 235, 1) !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  transition: transform 0.2s !important;
  margin-left: 2px !important;
  margin-right: 2px !important;
  background-image: var(--avatar-url) !important;
}

/* Industry Partners: Rounded rect */
.markdown-content :deep(a[data-rc-ref-id^="#rccompany-"]) {
  width: 1.5rem !important;
  height: 1.5rem !important;
  border-radius: 0.375rem !important;
  background-size: contain !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  border: 1px solid rgba(229, 231, 235, 1) !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  transition: transform 0.2s !important;
  margin-left: 4px !important;
  margin-right: 2px !important;
  background-image: var(--avatar-url) !important;
}

.dark .markdown-content :deep(a[data-rc-ref-id^="#rcauthor-"]),
.dark .markdown-content :deep(a[data-rc-ref-id^="#rccompany-"]) {
  border-color: rgba(55, 65, 81, 1) !important;
}

.markdown-content :deep(a[data-rc-ref-id^="#rcauthor-"]:hover),
.markdown-content :deep(a[data-rc-ref-id^="#rccompany-"]:hover) {
  transform: scale(1.1) !important;
}

/* Sources (Papers): Blue background, white text */
.markdown-content :deep(a[data-rc-ref-id^="#rcref-"]) {
  background-color: rgba(37, 99, 235, 1) !important;
  color: white !important;
  font-size: 12px !important;
  font-weight: 800 !important;
  padding: 0 0.1rem !important;
  margin: 0 3px !important;
  border-radius: 0.25rem !important;
  min-width: 1.4rem !important;
  height: 1.3rem !important;
  justify-content: center !important;
  transition: opacity 0.2s !important;
  cursor: pointer !important;
}

/* Patents: Purple background, white text */
.markdown-content :deep(a[data-rc-ref-id^="#rcpatent-"]) {
  background-color: rgba(147, 51, 234, 1) !important;
  color: white !important;
  font-size: 12px !important;
  font-weight: 800 !important;
  padding: 0 0.1rem !important;
  margin: 0 3px !important;
  border-radius: 0.25rem !important;
  min-width: 1.4rem !important;
  height: 1.3rem !important;
  justify-content: center !important;
  transition: opacity 0.2s !important;
  cursor: pointer !important;
}

.markdown-content :deep(a[data-rc-ref-id^="#rcref-"]:hover),
.markdown-content :deep(a[data-rc-ref-id^="#rcpatent-"]:hover) {
  opacity: 0.8 !important;
}

/* TABLE STYLING */
.markdown-content :deep(table) {
  box-sizing: border-box !important;
  width: calc(100% - 4px) !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  margin: 1.5rem 2px !important;
  border-radius: 0.75rem !important;
  border: 2px solid rgba(156, 163, 175, 1) !important;
  overflow: hidden !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
  background-color: rgba(255, 255, 255, 0.4) !important;
  backdrop-filter: blur(4px) !important;
  table-layout: auto !important;
}

.dark .markdown-content :deep(table) {
  border-color: rgba(75, 85, 99, 1) !important;
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.markdown-content :deep(th) {
  background-color: rgba(243, 244, 246, 0.8) !important;
  color: rgba(55, 65, 81, 1) !important;
  font-weight: 800 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 1rem !important;
  text-align: left !important;
  border-bottom: 2px solid rgba(156, 163, 175, 1) !important;
  border-right: 1px solid rgba(156, 163, 175, 0.5) !important;
  min-width: 150px !important;
  white-space: normal !important;
}

.dark .markdown-content :deep(th) {
  background-color: rgba(31, 41, 55, 0.8) !important;
  color: rgba(229, 231, 235, 1) !important;
  border-bottom-color: rgba(75, 85, 99, 1) !important;
  border-right-color: rgba(75, 85, 99, 0.5) !important;
}

.markdown-content :deep(td) {
  padding: 1rem !important;
  font-size: 0.875rem !important;
  border-bottom: 1px solid rgba(209, 213, 219, 0.5) !important;
  border-right: 1px solid rgba(209, 213, 219, 0.5) !important;
  vertical-align: top !important;
  color: rgba(55, 65, 81, 1) !important;
  line-height: 1.6 !important;
  min-width: 150px !important;
}

.dark .markdown-content :deep(td) {
  border-bottom-color: rgba(55, 65, 81, 0.5) !important;
  border-right-color: rgba(55, 65, 81, 0.5) !important;
  color: rgba(209, 213, 219, 1) !important;
}

/* First column is usually just a number, keep it narrow */
.markdown-content :deep(th:first-child),
.markdown-content :deep(td:first-child) {
  min-width: 50px !important;
  width: 50px !important;
  text-align: center !important;
}

/* Remove right border on last column */
.markdown-content :deep(th:last-child),
.markdown-content :deep(td:last-child) {
  border-right: none !important;
}

/* Remove bottom border on last row */
.markdown-content :deep(tr:last-child td) {
  border-bottom: none !important;
}

.markdown-content :deep(tr:hover td) {
  background-color: rgba(var(--theme-color), 0.1) !important;
}

.dark .markdown-content :deep(tr:hover td) {
  background-color: rgba(var(--theme-color), 0.05) !important;
}

/* THEME VARIABLES */
.theme-blue {
  --theme-color: 37, 99, 235;
}
.theme-emerald {
  --theme-color: 5, 150, 105;
}
.theme-purple {
  --theme-color: 147, 51, 234;
}
.theme-amber {
  --theme-color: 217, 119, 6;
}

/* CUSTOM SCROLLBAR */
.custom-scrollbar::-webkit-scrollbar:horizontal {
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track:horizontal {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:horizontal {
  background: rgba(var(--theme-color), 0.2);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:horizontal:hover {
  background: rgba(var(--theme-color), 0.4);
}
</style>
